import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Brain, Target, MessageSquare, ChevronRight, 
  BarChart, AlertTriangle, CheckCircle, Clock,
  ArrowRight, Star, Shield, Zap
} from 'lucide-react';

const translations = {
  es: {
    title: 'Simulación de Decisiones',
    subtitle: 'Practica la toma de decisiones en escenarios empresariales reales',
    sections: {
      scenarios: 'Escenarios',
      completed: 'Completados',
      analytics: 'Análisis'
    },
    actions: {
      startScenario: 'Comenzar escenario',
      submitDecision: 'Enviar decisión',
      viewAnalysis: 'Ver análisis',
      consultAI: 'Consultar IA'
    },
    feedback: {
      title: 'Análisis de Impacto',
      strengths: 'Fortalezas',
      improvements: 'Áreas de mejora',
      impact: 'Impacto en la empresa',
      score: 'Puntuación'
    }
  },
  en: {
    title: 'Decision Simulation',
    subtitle: 'Practice decision-making in real business scenarios',
    sections: {
      scenarios: 'Scenarios',
      completed: 'Completed',
      analytics: 'Analytics'
    },
    actions: {
      startScenario: 'Start scenario',
      submitDecision: 'Submit decision',
      viewAnalysis: 'View analysis',
      consultAI: 'Consult AI'
    },
    feedback: {
      title: 'Impact Analysis',
      strengths: 'Strengths',
      improvements: 'Areas for improvement',
      impact: 'Company Impact',
      score: 'Score'
    }
  }
};

// Mock scenarios
const mockScenarios = [
  {
    id: 1,
    title: 'Recorte de Presupuesto',
    description: 'El presupuesto del departamento se ha reducido un 30%. Debes decidir cómo ajustar los recursos y prioridades.',
    category: 'Resource Management',
    difficulty: 'advanced',
    timeLimit: '30 minutes',
    impact: ['Finance', 'Team Morale', 'Project Timeline'],
    status: 'available',
    options: [
      {
        id: 'a',
        text: 'Reducir el alcance de los proyectos manteniendo el equipo actual',
        consequences: {
          positive: ['Mantiene la moral del equipo', 'Preserva el conocimiento interno'],
          negative: ['Retraso en entregables', 'Posible impacto en calidad']
        },
        score: 85
      },
      {
        id: 'b',
        text: 'Reducir el equipo y mantener los proyectos prioritarios',
        consequences: {
          positive: ['Mantiene proyectos clave', 'Ahorro inmediato'],
          negative: ['Impacto en moral', 'Pérdida de talento']
        },
        score: 65
      },
      {
        id: 'c',
        text: 'Buscar financiamiento alternativo o patrocinios',
        consequences: {
          positive: ['Potencial para mantener operaciones', 'Nuevas oportunidades'],
          negative: ['Resultado incierto', 'Tiempo y esfuerzo adicional']
        },
        score: 90
      }
    ]
  },
  {
    id: 2,
    title: 'Crisis de Producción',
    description: 'Un error crítico ha sido descubierto en el sistema de producción. Debes decidir cómo manejarlo.',
    category: 'Crisis Management',
    difficulty: 'intermediate',
    timeLimit: '15 minutes',
    impact: ['Production', 'Customer Relations', 'Revenue'],
    status: 'available',
    options: [
      {
        id: 'a',
        text: 'Detener producción inmediatamente para investigar',
        consequences: {
          positive: ['Previene más problemas', 'Muestra compromiso con calidad'],
          negative: ['Pérdida inmediata de ingresos', 'Retrasos en entregas']
        },
        score: 80
      },
      {
        id: 'b',
        text: 'Implementar fix temporal mientras se investiga',
        consequences: {
          positive: ['Mantiene producción', 'Minimiza impacto inmediato'],
          negative: ['Riesgo de problemas futuros', 'Solución no óptima']
        },
        score: 70
      },
      {
        id: 'c',
        text: 'Continuar producción mientras se investiga en paralelo',
        consequences: {
          positive: ['No hay interrupción', 'Mantiene ingresos'],
          negative: ['Riesgo de más problemas', 'Posible daño reputacional']
        },
        score: 60
      }
    ]
  }
];

const DecisionSimulationPage = () => {
  const [activeSection, setActiveSection] = useState('scenarios');
  const [selectedScenario, setSelectedScenario] = useState<any | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleSubmitDecision = () => {
    if (!selectedOption) return;
    setShowResults(true);
  };

  const getSelectedOption = () => {
    if (!selectedScenario || !selectedOption) return null;
    return selectedScenario.options.find(opt => opt.id === selectedOption);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
            <p className="text-foreground/70">{t.subtitle}</p>
          </div>
          <button
            className="inline-flex items-center text-accent hover:text-accent/90"
            onClick={() => {}}
          >
            <MessageSquare className="h-5 w-5 mr-2" />
            {t.actions.consultAI}
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-border mb-8">
          <nav className="-mb-px flex space-x-8">
            {Object.entries(t.sections).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground/70 hover:text-foreground hover:border-border'
                }`}
              >
                {value}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Scenarios List */}
          <div className="lg:col-span-2 space-y-6">
            {!selectedScenario ? (
              mockScenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className="dark-card p-6 hover:border-accent/50 transition-all duration-300 cursor-pointer"
                  onClick={() => setSelectedScenario(scenario)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <Brain className="h-5 w-5 text-accent" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-foreground">{scenario.title}</h3>
                        <p className="text-foreground/70">{scenario.category}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      scenario.difficulty === 'advanced'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {scenario.difficulty}
                    </span>
                  </div>

                  <p className="mt-4 text-foreground/70">{scenario.description}</p>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-foreground/50 mr-1" />
                        <span className="text-sm text-foreground/70">{scenario.timeLimit}</span>
                      </div>
                      <div className="flex items-center">
                        <Target className="h-4 w-4 text-foreground/50 mr-1" />
                        <span className="text-sm text-foreground/70">{scenario.impact.join(', ')}</span>
                      </div>
                    </div>
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      {t.actions.startScenario}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="dark-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-foreground">{selectedScenario.title}</h2>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-foreground/50" />
                    <span className="text-foreground/70">{selectedScenario.timeLimit}</span>
                  </div>
                </div>

                <div className="bg-secondary/30 p-4 rounded-lg mb-6">
                  <p className="text-foreground">{selectedScenario.description}</p>
                </div>

                {!showResults ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-foreground">Opciones disponibles:</h3>
                    {selectedScenario.options.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleOptionSelect(option.id)}
                        className={`w-full text-left p-4 border rounded-lg transition-all ${
                          selectedOption === option.id
                            ? 'border-accent bg-accent/5'
                            : 'border-border hover:border-accent/50 hover:bg-secondary/30'
                        }`}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0 pt-0.5">
                            <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                              selectedOption === option.id
                                ? 'border-accent bg-accent text-white'
                                : 'border-border'
                            }`}>
                              {selectedOption === option.id && <CheckCircle className="h-3 w-3" />}
                            </div>
                          </div>
                          <p className="ml-3 text-foreground">{option.text}</p>
                        </div>
                      </button>
                    ))}

                    <div className="flex justify-end mt-6">
                      <button
                        onClick={handleSubmitDecision}
                        disabled={!selectedOption}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t.actions.submitDecision}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-accent/5 p-6 rounded-lg">
                      <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                        <Brain className="h-5 w-5 text-accent mr-2" />
                        {t.feedback.title}
                      </h3>

                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium text-green-600 mb-2">{t.feedback.strengths}</h4>
                          <ul className="space-y-2">
                            {getSelectedOption()?.consequences.positive.map((strength, index) => (
                              <li key={index} className="flex items-start">
                                <CheckCircle className="h-4 w-4 text-green-500 mt-1 mr-2" />
                                <span className="text-foreground">{strength}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-yellow-600 mb-2">{t.feedback.improvements}</h4>
                          <ul className="space-y-2">
                            {getSelectedOption()?.consequences.negative.map((improvement, index) => (
                              <li key={index} className="flex items-start">
                                <AlertTriangle className="h-4 w-4 text-yellow-500 mt-1 mr-2" />
                                <span className="text-foreground">{improvement}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="mt-6 p-4 bg-secondary/30 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-foreground/70">{t.feedback.score}</span>
                          <div className="flex items-center">
                            <Star className="h-5 w-5 text-yellow-500 mr-2" />
                            <span className="text-2xl font-bold text-foreground">
                              {getSelectedOption()?.score}/100
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4">
                      <button
                        onClick={() => {
                          setSelectedScenario(null);
                          setSelectedOption(null);
                          setShowResults(false);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30"
                      >
                        Volver a escenarios
                      </button>
                      <button
                        onClick={() => {
                          setSelectedOption(null);
                          setShowResults(false);
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90"
                      >
                        Intentar de nuevo
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Performance Stats */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Estadísticas</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-foreground/70">Decisiones tomadas</span>
                    <span className="text-sm font-medium text-foreground">24</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div className="h-2 bg-accent rounded-full" style={{ width: '80%' }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-foreground/70">Puntuación media</span>
                    <span className="text-sm font-medium text-foreground">85/100</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div className="h-2 bg-green-500 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-foreground/70">Tiempo promedio</span>
                    <span className="text-sm font-medium text-foreground">12m 30s</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div className="h-2 bg-yellow-500 rounded-full" style={{ width: '65%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Skills Impact */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Impacto en Habilidades</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 text-accent mr-2" />
                    <span className="text-foreground">Toma de decisiones</span>
                  </div>
                  <span className="text-green-500">+15%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Target className="h-5 w-5 text-accent mr-2" />
                    <span className="text-foreground">Análisis de riesgos</span>
                  </div>
                  <span className="text-green-500">+12%</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Zap className="h-5 w-5 text-accent mr-2" />
                    <span className="text-foreground">Resolución de problemas</span>
                  </div>
                  <span className="text-green-500">+18%</span>
                </div>
              </div>
            </div>

            {/* Next Challenge */}
            <div className="dark-card p-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Próximo Desafío</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Brain className="h-8 w-8 text-accent" />
                  <div className="ml-3">
                    <p className="font-medium text-foreground">Gestión de Crisis</p>
                    <p className="text-sm text-foreground/70">Disponible en 2 horas</p>
                  </div>
                </div>
                <button className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                  Recordarme
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionSimulationPage;